// src/hooks/useStudyOnboarding.ts
// =====================================================================
// PROGRESSIVE ONBOARDING — Sistema adaptativo de micro-orientações
// Ensina enquanto a pessoa usa, no momento certo, uma única vez.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';

// =====================================================================
// TIPOS
// =====================================================================

export type OnboardingStep =
    | 'welcome'        // Camada 1: overlay inicial
    | 'rating'         // Aparece quando vira o 1º card
    | 'menu'           // Aparece quando abre o menu pela 1ª vez
    | 'details'        // Aparece após 3 cards
    | 'sidebar'        // Aparece após 5 cards
    | 'done';          // Tudo concluído

interface OnboardingState {
    dismissed: boolean;          // "Não mostrar novamente" foi marcado
    completedSteps: OnboardingStep[];
    cardsAnswered: number;       // Contador de cards respondidos na sessão
}

const STORAGE_KEY = 'graia_study_onboarding';

// =====================================================================
// HOOK PRINCIPAL
// =====================================================================

export function useStudyOnboarding() {
    const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [neverShowAgain, setNeverShowAgain] = useState(false);
    const [cardsAnswered, setCardsAnswered] = useState(0);
    const [menuOpenedOnce, setMenuOpenedOnce] = useState(false);

    // ===== CARREGAR ESTADO PERSISTIDO =====
    const loadState = useCallback((): OnboardingState => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return { dismissed: false, completedSteps: [], cardsAnswered: 0 };
            return JSON.parse(raw);
        } catch {
            return { dismissed: false, completedSteps: [], cardsAnswered: 0 };
        }
    }, []);

    const saveState = useCallback((state: Partial<OnboardingState>) => {
        try {
            const current = loadState();
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...state }));
        } catch {
            // silencioso
        }
    }, [loadState]);

    const hasCompleted = useCallback((step: OnboardingStep): boolean => {
        const state = loadState();
        return state.dismissed || state.completedSteps.includes(step);
    }, [loadState]);

    const markCompleted = useCallback((step: OnboardingStep) => {
        const state = loadState();
        if (!state.completedSteps.includes(step)) {
            saveState({ completedSteps: [...state.completedSteps, step] });
        }
    }, [loadState, saveState]);

    // ===== INICIALIZAÇÃO: mostrar welcome se nunca viu =====
    useEffect(() => {
        const state = loadState();
        if (state.dismissed) return;

        if (!state.completedSteps.includes('welcome')) {
            // Pequeno delay para o componente montar
            const timer = setTimeout(() => {
                setCurrentStep('welcome');
                setIsVisible(true);
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [loadState]);

    // ===== CAMADA 1: Fechar welcome =====
    const dismissWelcome = useCallback((permanent: boolean) => {
        setIsVisible(false);
        markCompleted('welcome');

        if (permanent) {
            saveState({ dismissed: true });
            setCurrentStep('done');
            return;
        }

        setCurrentStep(null);
    }, [markCompleted, saveState]);

    // ===== CAMADA 2: Usuário virou o primeiro card =====
    const onCardFlipped = useCallback(() => {
        const state = loadState();
        if (state.dismissed) return;
        if (hasCompleted('rating')) return;
        if (currentStep !== null) return; // outro tooltip aberto

        setCurrentStep('rating');
        setIsVisible(true);
    }, [loadState, hasCompleted, currentStep]);

    // ===== CAMADA 2: Usuário respondeu um card =====
    const onCardAnswered = useCallback(() => {
        const newCount = cardsAnswered + 1;
        setCardsAnswered(newCount);

        const state = loadState();
        if (state.dismissed) return;

        // Após 3 cards: mostrar tooltip de "Mais detalhes"
        if (newCount === 3 && !hasCompleted('details')) {
            setTimeout(() => {
                setCurrentStep('details');
                setIsVisible(true);
            }, 400);
            return;
        }

        // Após 5 cards: mostrar tooltip da sidebar
        if (newCount === 5 && !hasCompleted('sidebar')) {
            setTimeout(() => {
                setCurrentStep('sidebar');
                setIsVisible(true);
            }, 400);
        }
    }, [cardsAnswered, loadState, hasCompleted]);

    // ===== CAMADA 2: Usuário abriu o menu =====
    const onMenuOpened = useCallback(() => {
        if (menuOpenedOnce) return;
        setMenuOpenedOnce(true);

        const state = loadState();
        if (state.dismissed) return;
        if (hasCompleted('menu')) return;
        if (currentStep !== null) return;

        setTimeout(() => {
            setCurrentStep('menu');
            setIsVisible(true);
        }, 300);
    }, [menuOpenedOnce, loadState, hasCompleted, currentStep]);

    // ===== FECHAR QUALQUER TOOLTIP =====
    const dismissStep = useCallback((step: OnboardingStep, permanent?: boolean) => {
        setIsVisible(false);
        markCompleted(step);

        if (permanent) {
            saveState({ dismissed: true });
        }

        setTimeout(() => {
            setCurrentStep(null);
        }, 300);
    }, [markCompleted, saveState]);

    // ===== RESETAR (para testes/admin) =====
    const resetOnboarding = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setCurrentStep(null);
        setIsVisible(false);
        setCardsAnswered(0);
        setMenuOpenedOnce(false);
    }, []);

    return {
        currentStep,
        isVisible,
        neverShowAgain,
        setNeverShowAgain,
        onCardFlipped,
        onCardAnswered,
        onMenuOpened,
        dismissWelcome,
        dismissStep,
        resetOnboarding,
        hasCompleted,
    };
}